import { Col, Popover, Row, Table } from "antd";
import { Trans, useTranslation } from "react-i18next";
import { Charts } from "shared/components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp } from "@fortawesome/free-solid-svg-icons";
import { ReactComponent as FollowersIcon } from "shared/assets/images/followers-icon.svg";
import { ReactComponent as RocketIcon } from "shared/assets/images/rocket-icon.svg";
import { capitalize } from "lodash";
import { InfoCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import MouseDisclaimer from "./components/MouseDisclaimer";

interface CareerCatalogAnalysisProps {
  artistPositionInRankByFilter: any;
  releasePolynomialCurveTrend: any;
  lastReleases: any;
  phonographicCatalogAnalysis: any;
  spotify: boolean;
}
export const releaseColumns = (t: any) => [
  {
    title: t("pages.CareerDiagnose.trackTitle"),
    dataIndex: "albumName",
    width: "30%",
  },
  {
    title: t("pages.CareerDiagnose.trackDate"),
    dataIndex: ["releaseDate", "dateTruncated"],
    render: (releaseDate: any) => moment(releaseDate).format("DD/MM/YYYY"),
    width: "10%",
  },
  {
    title: t("pages.CareerDiagnose.type"),
    dataIndex: "albumType",
    render: (text: any) => capitalize(text),
    width: "5%",
  },
  {
    title: t("pages.CareerDiagnose.label"),
    dataIndex: "label",
    width: "15%",
  },
  {
    title: t("pages.CareerDiagnose.popularity"),
    dataIndex: "popularity",
    // align: "center",
    width: "5%",
  },
];

const CareerCatalogAnalysis: React.FC<CareerCatalogAnalysisProps> = (props) => {
  const {
    releasePolynomialCurveTrend,
    lastReleases,
    phonographicCatalogAnalysis,
    spotify,
  } = props;
  // const history = useHistory();
  const formatDate = (date: string) => {
    const momentDate = moment(date);
    return momentDate.isValid() ? momentDate.format("DD/MM/YYYY") : "-";
  };

  const formatNumber = (number: number) =>
    Number.isFinite(number) ? number.toFixed(1) : "-";

  const { t } = useTranslation("radar");

  const showReleases = releasePolynomialCurveTrend?.data?.length > 2;

  const hidePopularityFeatVsSolo =
    (phonographicCatalogAnalysis?.popularitySingle === null &&
      phonographicCatalogAnalysis?.popularityAlbum === null) ||
    false;

  const hidePopularitySingleVsAlbum =
    (phonographicCatalogAnalysis?.popularitySolo === null &&
      phonographicCatalogAnalysis?.popularityFeat === null) ||
    false;

  return (
    <div className="analysis-of-phonographic-catalog">
      <h1 className="texto-colorido title">
        {t("pages.CareerDiagnose.phonographicCatalogTitle")}
      </h1>
      <p style={{ textAlign: "justify", fontSize: 19 }}>
        <Trans
          i18nKey={`pages.CareerDiagnose.phonographicCatalogText`}
          ns="radar"
        />
      </p>
      {!spotify && (
        <div className="blur-img" style={{ top: 700 }}>
          <div className="modal-inside-blur-img">
            <p className="message-modal">
              <Trans
                i18nKey="pages.CareerDiagnose.withoutSpotifyDisclaimer"
                ns="radar"
              />
            </p>
          </div>
        </div>
      )}
      <div className="info-metrics">
        <Row gutter={96} className="info-metrics">
          <Col xs={6}>
            <div className="card" style={{ height: 250 }}>
              <div
                style={{ display: "flex", width: "100%", padding: "0 12px" }}
              >
                <div style={{ flex: 1, marginLeft: 20, width: 50 }}>
                  <RocketIcon className="main-icon" />
                </div>
                <Popover
                  showArrow={false}
                  content={
                    <>{t("pages.CareerDiagnose.releaseFrequencyDisclaimer")}</>
                  }
                  placement="bottom"
                >
                  <InfoCircleOutlined
                    style={{
                      fontSize: 20,
                      alignSelf: "center",
                    }}
                  />
                </Popover>
              </div>
              {t("pages.CareerDiagnose.releaseFrequencyMessage")}
              <span style={{ color: "#B692F6" }}>
                {t("pages.CareerDiagnose.manyDays", {
                  days: phonographicCatalogAnalysis?.releaseFreqDays,
                })}
              </span>
              <span
                className={`${
                  phonographicCatalogAnalysis?.releaseIsUnderAverage
                    ? "red"
                    : "green"
                } card-text`}
              >
                {phonographicCatalogAnalysis?.releaseIsUnderAverage ? (
                  <>
                    <FontAwesomeIcon icon={faArrowDown} />
                    {` ${Math.abs(
                      phonographicCatalogAnalysis?.releasePercentage
                    )}%`}
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faArrowUp} />
                    {` ${Math.abs(
                      phonographicCatalogAnalysis?.releasePercentage
                    )}%`}
                  </>
                )}
              </span>
              <span className="font-light">
                {phonographicCatalogAnalysis?.releaseIsUnderAverage
                  ? t("pages.ArtistProfile.belowAverage")
                  : t("pages.ArtistProfile.aboveAverage")}
              </span>
            </div>
          </Col>
          <Col xs={6}>
            <div className="card" style={{ height: 250 }}>
              <div
                style={{ display: "flex", width: "100%", padding: "0 12px" }}
              >
                <div style={{ flex: 1, marginLeft: 20, width: 50 }}>
                  <RocketIcon className="main-icon" />
                </div>
                <Popover
                  showArrow={false}
                  content={
                    <>
                      {t("pages.CareerDiagnose.lastTwelveMonthsDoneDisclaimer")}
                    </>
                  }
                  placement="bottom"
                >
                  <InfoCircleOutlined
                    style={{
                      fontSize: 20,
                      alignSelf: "center",
                    }}
                  />
                </Popover>
              </div>
              {t("pages.CareerDiagnose.releaseFrequency12MonthsMessage")}
              <span style={{ color: "#B692F6" }}>
                {t("pages.CareerDiagnose.releaseFrequency12Months", {
                  releases:
                    phonographicCatalogAnalysis?.releasesLast12MonthsTotal,
                })}
              </span>
              <span
                className={`${
                  phonographicCatalogAnalysis?.releaseLast12MonthsIsUnderAverage
                    ? "red"
                    : "green"
                } card-text`}
              >
                {phonographicCatalogAnalysis?.releaseLast12MonthsIsUnderAverage ? (
                  <>
                    <FontAwesomeIcon icon={faArrowDown} />
                    {` ${Math.abs(
                      phonographicCatalogAnalysis?.releaseLast12MonthsPercentage
                    )}%`}
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faArrowUp} />
                    {` ${Math.abs(
                      phonographicCatalogAnalysis?.releaseLast12MonthsPercentage
                    )}%`}
                  </>
                )}
              </span>
              <span className="font-light">
                {phonographicCatalogAnalysis?.releaseLast12MonthsIsUnderAverage
                  ? t("pages.ArtistProfile.belowAverage")
                  : t("pages.ArtistProfile.aboveAverage")}
              </span>
            </div>
          </Col>
          <Col xs={6}>
            {hidePopularitySingleVsAlbum && <div className="hide-card" />}
            <div className="card blur" style={{ height: 250 }}>
              <div
                style={{ display: "flex", width: "100%", padding: "0 12px" }}
              >
                <div style={{ flex: 1, marginLeft: 20, width: 50 }}>
                  <FollowersIcon className="main-icon" />
                </div>
                <Popover
                  showArrow={false}
                  content={
                    <>
                      {t("pages.CareerDiagnose.popularityFeatVsSoloDisclaimer")}
                    </>
                  }
                  placement="bottom"
                >
                  <InfoCircleOutlined
                    style={{
                      fontSize: 20,
                      alignSelf: "center",
                    }}
                  />
                </Popover>
              </div>
              <Trans
                i18nKey={`pages.CareerDiagnose.popularityFeatVsSolo`}
                ns="radar"
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-around",
                  width: "100%",
                  fontSize: 32,
                }}
              >
                <span>
                  {formatNumber(phonographicCatalogAnalysis?.popularityFeat)}
                </span>
                <span>
                  {formatNumber(phonographicCatalogAnalysis?.popularitySolo)}
                </span>
              </div>
              <span style={{ color: "#B692F6" }}>
                {phonographicCatalogAnalysis?.percentageBetweenPopularityFeatAndPopularitySolo ||
                  "-"}
                %
              </span>
              <span className="font-light" style={{ fontSize: 12 }}>
                {t("pages.CareerDiagnose.differencePercentage")}
              </span>
            </div>
          </Col>
          <Col xs={6}>
            {hidePopularityFeatVsSolo && <div className="hide-card" />}
            <div className="card blur" style={{ height: 250 }}>
              <div
                style={{ display: "flex", width: "100%", padding: "0 12px" }}
              >
                <div style={{ flex: 1, marginLeft: 20, width: 50 }}>
                  <FollowersIcon className="main-icon" />
                </div>
                <Popover
                  showArrow={false}
                  content={
                    <>
                      {t(
                        "pages.CareerDiagnose.popularitySingleVsAlbumDisclaimer"
                      )}
                    </>
                  }
                  placement="bottom"
                >
                  <InfoCircleOutlined
                    style={{
                      fontSize: 20,
                      alignSelf: "center",
                    }}
                  />
                </Popover>
              </div>
              <Trans
                i18nKey={`pages.CareerDiagnose.popularitySingleVsAlbum`}
                ns="radar"
              />

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-around",
                  width: "100%",
                  fontSize: 32,
                }}
              >
                <span>
                  {formatNumber(phonographicCatalogAnalysis?.popularitySingle)}
                </span>
                <span>
                  {formatNumber(phonographicCatalogAnalysis?.popularityAlbum)}
                </span>
              </div>
              <span style={{ color: "#B692F6" }}>
                {phonographicCatalogAnalysis?.percentageBetweenPopularitySingleAndPopularityAlbum ||
                  "-"}
                %
              </span>
              <span className="font-light" style={{ fontSize: 12 }}>
                {t("pages.CareerDiagnose.differencePercentage")}
              </span>
            </div>
          </Col>
          <Col xs={24}>
            <div
              style={{
                display: "flex",
                gap: 12,
                marginTop: 32,
                flexDirection: "row",
                backgroundColor: "#000",
                padding: "24px 0",
              }}
              className="card"
            >
              <div className="info-fact-container">
                {t("pages.CareerDiagnose.releases")}
                <span
                  style={{
                    display: "block",
                    fontSize: 14,
                    textTransform: "none",
                  }}
                >
                  {t("pages.CareerDiagnose.releasesDisclaimer")}
                </span>
              </div>
              <div className="info-fact-container">
                <h3 className="info-fact-label">
                  {t("pages.CareerDiagnose.firstRelease")}
                </h3>
                <span className="info-fact-value">
                  {formatDate(phonographicCatalogAnalysis?.firstRelease)}
                </span>
              </div>
              <div className="info-fact-container">
                <h3 className="info-fact-label">
                  {t("pages.CareerDiagnose.lastRelease")}
                </h3>
                <span className="info-fact-value">
                  {formatDate(phonographicCatalogAnalysis?.lastRelease)}
                </span>
              </div>
              <div className="info-fact-container">
                <h3 className="info-fact-label">
                  {t("pages.CareerDiagnose.totalRelease")}
                </h3>
                <span className="info-fact-value">
                  {phonographicCatalogAnalysis?.totalReleases || "-"}
                </span>
              </div>
            </div>
          </Col>
          <Col xs={24}>
            <div
              style={{
                display: "flex",
                gap: 12,
                marginTop: 32,
                flexDirection: "row",
                backgroundColor: "#000",
                padding: "24px 0",
              }}
              className="card"
            >
              <div className="info-fact-container">
                {t("pages.CareerDiagnose.collabs")}
                <span
                  style={{
                    display: "block",
                    fontSize: 14,
                    textTransform: "none",
                  }}
                >
                  {t("pages.CareerDiagnose.collabsDisclaimer")}
                </span>
              </div>
              <div className="info-fact-container">
                <h3 className="info-fact-label">
                  {t("pages.CareerDiagnose.main")}
                </h3>
                <span className="info-fact-value">
                  {phonographicCatalogAnalysis?.protagonistCollab || "-"}
                </span>
              </div>
              <div className="info-fact-container">
                <h3 className="info-fact-label">
                  {t("pages.CareerDiagnose.guest")}
                </h3>
                <span className="info-fact-value">
                  {phonographicCatalogAnalysis?.invitedCollab || "-"}
                </span>
              </div>
              <div className="info-fact-container">
                <h3 className="info-fact-label">
                  {t("pages.CareerDiagnose.totalRelease")}
                </h3>
                <span className="info-fact-value">
                  {phonographicCatalogAnalysis?.totalCollab || "-"}
                </span>
              </div>
            </div>
          </Col>
        </Row>
        <div>
          <div
            style={{
              display: "flex",
              marginTop: 32,
              justifyContent: "space-between",
            }}
          >
            <h1>{t("pages.CareerDiagnose.popularityReleasesTrend")}</h1>{" "}
            {showReleases && <MouseDisclaimer style={{ textAlign: "end" }} />}
          </div>

          <div style={{ color: "#000" }} className="chart">
            <Charts.ScatterChart
              scatterData={releasePolynomialCurveTrend?.data}
              lineData={releasePolynomialCurveTrend?.polynomialCurve}
              tooltip={{
                points: releasePolynomialCurveTrend?.data,
                title: t("pages.ArtistProfile.release"),
                name: t("pages.ArtistProfile.name"),
                release: t("pages.ArtistProfile.releaseDate"),
                ageInMonths: t("pages.ArtistProfile.ageInMonths"),
                popularity: t("pages.ArtistProfile.popularity"),
                type: t("pages.ArtistProfile.type"),
              }}
              containerHeight={400}
              horizontalLegendAlign="center"
              customLegendItems={[t("pages.ArtistProfile.releaseTrendLegend")]}
            />
          </div>
          <span
            style={{
              display: "block",
              width: "100%",
              textAlign: "end",
              marginBottom: 32,
              fontSize: 16,
            }}
          >
            {!showReleases && (
              <Trans
                i18nKey="pages.CareerDiagnose.releaseTrendMessage"
                ns="radar"
              />
            )}
          </span>
        </div>
        <h1 className="title">
          {t("pages.CareerDiagnose.topReleasesByPopularity")}
        </h1>
        <Table
          columns={releaseColumns(t)}
          pagination={false}
          dataSource={lastReleases?.artistReleases?.slice(0, 5)}
        />
      </div>
    </div>
  );
};

export default CareerCatalogAnalysis;
